import {
    View,
    ButtonView,
    LabeledFieldView,
    ViewCollection,
    createLabeledInputText,
    icons,
    submitHandler,
    FocusTracker,
    FocusCycler,
    KeystrokeHandler,
} from 'ckeditor5'

/**
 * The unsplash form view controller class.
 *
 * See {@link module:unsplash/ui/searchFormView~SearchFormView}.
 *
 * @extends module:ui/view~View
 */
export default class SearchFormView extends View {
    searchField;
    searchButtonView;
    childViews;

    constructor(locale) {
        super( locale );

        /**
		 * Tracks information about DOM focus in the form.
		 *
		 * @readonly
		 * @member {module:utils/focustracker~FocusTracker}
		 */
		this.focusTracker = new FocusTracker();

        /**
		 * An instance of the {@link module:utils/keystrokehandler~KeystrokeHandler}.
		 *
		 * @readonly
		 * @member {module:utils/keystrokehandler~KeystrokeHandler}
		 */
		this.keystrokes = new KeystrokeHandler();

        /** Creating form elements */
        /**
		 * The Unsplash photo query input view.
		 *
		 * @member {module:ui/labeledfield/labeledfieldview~LabeledFieldView}
		 */
        this.searchField = this._createSearchInput();
        
        /**
		 * The search button view.
		 *
		 * @member {module:ui/button/buttonview~ButtonView}
		 */
        this.searchButtonView = this._createSearchButton();
        this.searchButtonView.type = 'submit';

        /**
		 * A collection of views that can be focused in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/viewcollection~ViewCollection}
		 */
		this._focusables = new ViewCollection();

		/**
		 * Helps cycling over {@link #_focusables} in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/focuscycler~FocusCycler}
		 */
		this._focusCycler = new FocusCycler( {
			focusables: this._focusables,
			focusTracker: this.focusTracker,
			keystrokeHandler: this.keystrokes,
			actions: {
				// Navigate form fields backwards using the Shift + Tab keystroke.
				focusPrevious: 'shift + tab',
				// Navigate form fields forwards using the Tab key.
				focusNext: 'tab'
			}
		} );

        /**
		 * The Unsplash search form view.
		 *
		 * @member {module:ui/view~View}
		 */
        this.setTemplate({
            tag: 'form',
            attributes: {
                class: [ 'ck', 'ck-unsplash-form'],
                tabindex: '-1',
            },
            children: [
                this.searchField,
                this.searchButtonView,
            ],
        })
    }

    /**
	 * @inheritDoc
	 */
    render() {
        super.render();

        submitHandler({
            view: this,
        })

        const childViews = [
            this.searchField,
            this.searchButtonView,
        ];

        childViews.forEach( v => {
            // Register the view as focusable.
            this._focusables.add( v );
            // Register the view in the focus tracker.
            this.focusTracker.add( v.element );
        } );

        // Start listening for the keystrokes coming from #element.
        this.keystrokes.listenTo( this.element );
    }

    /**
	 * Focuses the fist {@link #_focusables} in the form.
	 */
    focus() {
		this._focusCycler.focusFirst();
    }

     /**
	 * Creates a labeled input view.
	 *
	 * @private
	 * @returns {module:ui/labeledfield/labeledfieldview~LabeledFieldView} Labeled field view instance.
	 */
    _createSearchInput() {
        const t = this.locale.t
        const labeledInput = new LabeledFieldView( this.locale, createLabeledInputText );
        labeledInput.label = t( 'Search Unsplash' )
        
        return labeledInput;
    }

     /**
	 * Creates a button view.
	 *
	 * @private
	 * @returns {module:ui/button/buttonview~ButtonView} The button view instance.
	 */
    _createSearchButton() {
        const button = new ButtonView();

        button.set({
            icon: icons.check,
            class: 'ck-button-save',
        })

        return button;
    }
}